import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Pagination } from '@/components/molecules';
import { Modal } from '@/components/molecules/Modal';
import { ReportFilters, ReportSummaryCards, ReportTable } from '@/components/organisms/Reports';
import type { TableColumn } from '@/components/organisms/Reports';
import { useInventoryReport, useExportReportMutation, useWarehousesList } from '@/hooks/useErpApi';
import type { InventoryReportFilter, InventoryReportRecord } from '@kisanlink/erp-service';

export default function InventoryReportPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<InventoryReportFilter>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [activeTab, setActiveTab] = useState<'all' | 'low_stock' | 'expiring'>('all');
  const [selectedRecord, setSelectedRecord] = useState<InventoryReportRecord & { days_on_shelf?: number } | null>(null);
  
  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;

  const queryFilters: InventoryReportFilter = {
    ...filters,
    limit: itemsPerPage,
    offset,
    ...(activeTab === 'low_stock' && { low_stock: true }),
    ...(activeTab === 'expiring' && { expiring_soon: true }),
  };

  const { data: report, isLoading, error, refetch } = useInventoryReport(queryFilters);
  const { data: warehouses } = useWarehousesList({});
  const { mutateAsync: exportReport, isLoading: isExporting } = useExportReportMutation();

  const warehouseOptions = (warehouses || []).map((w) => ({ id: w.id, name: w.name }));

  const handleFilterChange = useCallback((newFilters: Record<string, unknown>) => {
    setFilters(newFilters as InventoryReportFilter);
    setCurrentPage(1);
  }, []);
  
  const handleTabChange = (tab: 'all' | 'low_stock' | 'expiring') => {
    setActiveTab(tab);
    setCurrentPage(1);
  };
  
  // Get total count from API response if available
  const totalItems = report?.pagination?.total || (report?.records?.length || 0);

  const handleExport = useCallback(
    async (format: 'xlsx' | 'pdf') => {
      try {
        const blob = await exportReport({
          reportType: 'inventory',
          filters: { ...queryFilters, format },
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `inventory-report.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Export failed:', err);
      }
    },
    [queryFilters, exportReport]
  );

  const getExpiryBadge = (daysToExpiry?: number) => {
    if (daysToExpiry === undefined) return null;
    if (daysToExpiry <= 0) return <Badge variant="error">Expired</Badge>;
    if (daysToExpiry <= 7) return <Badge variant="error">{daysToExpiry}d left</Badge>;
    if (daysToExpiry <= 30) return <Badge variant="warning">{daysToExpiry}d left</Badge>;
    return <Badge variant="success">{daysToExpiry}d left</Badge>;
  };

  const columns: TableColumn<InventoryReportRecord>[] = [
    {
      key: 'product_name',
      header: 'Product',
      render: (row) => (
        <div>
          <div className="font-medium text-neutral-900">{row.product_name}</div>
          <div className="text-xs text-neutral-500 font-mono whitespace-nowrap">{row.variant_sku}</div>
        </div>
      ),
    },
    {
      key: 'warehouse_name',
      header: 'Warehouse',
      render: (row) => row.warehouse_name,
    },
    {
      key: 'batch_id',
      header: 'Batch',
      className: 'min-w-[120px]',
      render: (row) => (
        <span className="font-mono text-xs text-neutral-600 whitespace-nowrap">{row.batch_id}</span>
      ),
    },
    {
      key: 'total_quantity',
      header: 'Quantity',
      render: (row) => (
        <span
          className={row.total_quantity > 0 ? 'text-success-700 font-medium' : 'text-error-600'}
        >
          {row.total_quantity.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'cost_price',
      header: 'Cost Price',
      render: (row) => `₹${row.cost_price.toLocaleString()}`,
    },
    {
      key: 'total_value',
      header: 'Value',
      render: (row) => (
        <span className="font-medium text-neutral-900">₹{row.total_value.toLocaleString()}</span>
      ),
    },
    {
      key: 'expiry_date',
      header: 'Expiry',
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.expiry_date ? (
            <>
              <span className="text-sm">{new Date(row.expiry_date).toLocaleDateString()}</span>
              {getExpiryBadge(row.days_to_expiry)}
            </>
          ) : (
            '-'
          )}
        </div>
      ),
    },
    {
      key: 'days_on_shelf',
      header: 'Shelf Life',
      render: (row) => {
        const daysOnShelf = (row as any).days_on_shelf;
        return daysOnShelf !== undefined ? (
          <span className="text-sm text-neutral-900">{daysOnShelf} days</span>
        ) : (
          <span className="text-sm text-neutral-400">-</span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedRecord(row as InventoryReportRecord & { days_on_shelf?: number })}
          className="text-neutral-600 hover:text-primary-600"
        >
          <Icon name="Eye" size={16} />
        </Button>
      ),
    },
  ];

  const summary = report?.summary;
  const summaryCards = summary
    ? [
        {
          label: 'Total Batches',
          value: summary.total_batches,
          icon: 'Box' as const,
          color: 'primary' as const,
          format: 'number' as const,
        },
        {
          label: 'Total Quantity',
          value: summary.total_quantity,
          icon: 'Package' as const,
          color: 'info' as const,
          format: 'number' as const,
        },
        {
          label: 'Total Value',
          value: summary.total_value,
          icon: 'IndianRupee' as const,
          color: 'success' as const,
          format: 'currency' as const,
        },
        {
          label: 'Low Stock Items',
          value: summary.low_stock_count,
          icon: 'TriangleAlert' as const,
          color: 'warning' as const,
          format: 'number' as const,
        },
      ]
    : [];

  // Pagination is handled by the Pagination component below

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <Icon name="ArrowLeft" size={16} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Inventory Report</h1>
            <p className="text-neutral-600 text-sm mt-1">
              Inventory batches with stock levels and expiry tracking
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <ReportFilters
        showDateRange={false}
        showSearch={false}
        showWarehouse={true}
        warehouses={warehouseOptions}
        onFilterChange={handleFilterChange}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {/* Quick Filters Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
        <div className="flex gap-2 flex-wrap">
          {(
            [
              { key: 'all', label: 'All Inventory' },
              { key: 'low_stock', label: 'Low Stock' },
              { key: 'expiring', label: 'Expiring Soon' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                activeTab === tab.key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      {!isLoading && !error && summary && <ReportSummaryCards cards={summaryCards} />}

      {/* Report Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <ReportTable
          columns={columns}
          data={report?.records || []}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          emptyMessage="No inventory batches found matching your criteria"
        />
        {!isLoading && !error && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            disabled={isLoading}
          />
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedRecord}
        onClose={() => setSelectedRecord(null)}
        title="Inventory Batch Details"
        size="lg"
      >
        {selectedRecord && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-neutral-500 mb-1">Batch ID</p>
                <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.batch_id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Warehouse</p>
                <p className="text-sm font-medium text-neutral-900">{selectedRecord.warehouse_name}</p>
                <p className="text-xs text-neutral-500 font-mono">{selectedRecord.warehouse_id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Product Name</p>
                <p className="text-sm font-medium text-neutral-900">{selectedRecord.product_name}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Variant SKU</p>
                <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.variant_sku}</p>
                <p className="text-xs text-neutral-500 font-mono">{selectedRecord.variant_id}</p>
              </div>
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Quantity Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Total Quantity</p>
                  <p className={`text-sm font-medium ${selectedRecord.total_quantity > 0 ? 'text-success-700' : 'text-error-600'}`}>
                    {selectedRecord.total_quantity.toLocaleString()}
                  </p>
                </div>
                {(selectedRecord as any).reserved_quantity !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Reserved Quantity</p>
                    <p className="text-sm font-medium text-neutral-900">{(selectedRecord as any).reserved_quantity.toLocaleString()}</p>
                  </div>
                )}
                {(selectedRecord as any).available_quantity !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Available Quantity</p>
                    <p className="text-sm font-medium text-success-700">{(selectedRecord as any).available_quantity.toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Pricing Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Cost Price</p>
                  <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.cost_price.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Total Value</p>
                  <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.total_value.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Expiry & Shelf Life</h3>
              <div className="grid grid-cols-2 gap-4">
                {selectedRecord.expiry_date && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Expiry Date</p>
                    <p className="text-sm font-medium text-neutral-900">{new Date(selectedRecord.expiry_date).toLocaleDateString()}</p>
                    {selectedRecord.days_to_expiry !== undefined && (
                      <div className="mt-1">{getExpiryBadge(selectedRecord.days_to_expiry)}</div>
                    )}
                  </div>
                )}
                {selectedRecord.days_to_expiry !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Days to Expiry</p>
                    <p className="text-sm font-medium text-neutral-900">{selectedRecord.days_to_expiry} days</p>
                  </div>
                )}
                {selectedRecord.days_on_shelf !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Shelf Life</p>
                    <p className="text-sm font-medium text-neutral-900">{selectedRecord.days_on_shelf} days</p>
                  </div>
                )}
                {(selectedRecord as any).batch_number && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Batch Number</p>
                    <p className="text-sm font-medium text-neutral-900">{(selectedRecord as any).batch_number}</p>
                  </div>
                )}
                {(selectedRecord as any).received_date && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Received Date</p>
                    <p className="text-sm text-neutral-900">{new Date((selectedRecord as any).received_date).toLocaleDateString()}</p>
                  </div>
                )}
                {(selectedRecord as any).quality_status && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Quality Status</p>
                    <Badge variant={(selectedRecord as any).quality_status === 'accepted' ? 'success' : 'warning'}>
                      {(selectedRecord as any).quality_status}
                    </Badge>
                  </div>
                )}
                {(selectedRecord as any).source_grn_id && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Source GRN ID</p>
                    <p className="text-sm font-medium text-neutral-900 font-mono">{(selectedRecord as any).source_grn_id}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Tax Information</h3>
              <div className="grid grid-cols-2 gap-4">
                {selectedRecord.cgst_rate !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">CGST Rate</p>
                    <p className="text-sm text-neutral-900">{selectedRecord.cgst_rate}%</p>
                  </div>
                )}
                {selectedRecord.sgst_rate !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">SGST Rate</p>
                    <p className="text-sm text-neutral-900">{selectedRecord.sgst_rate}%</p>
                  </div>
                )}
                {selectedRecord.is_tax_exempt !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Tax Exempt</p>
                    <Badge variant={selectedRecord.is_tax_exempt ? 'info' : 'neutral'}>
                      {selectedRecord.is_tax_exempt ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Timestamps</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Created At</p>
                  <p className="text-sm text-neutral-900">{new Date(selectedRecord.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Updated At</p>
                  <p className="text-sm text-neutral-900">{new Date(selectedRecord.updated_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
